import { Button, MoreMenu, OrderStatusChip, Text } from '@shared/components';
import { useToast } from '@shared/components/toast';
import { UNAVAILABLE } from '@shared/constants/placeholderText';
import {
  JobType,
  OrderSummaryFragment,
  useDeleteOrderMutation,
  useReplicateOrderMutation,
} from '@shared/generated/graphql';
import { JobIconWithPadding } from '@shared/plugin/components/JobIcon';
import { useNavigateWithReturn } from '@shared/plugin/hooks/useReturnRoute';
import { getElementSeparator } from '@shared/plugin/utils/intersperseElements';
import { ORDERS_ROUTE } from 'clerk_common/constants/urls/routes';
import {
  formatDeliveryCityState,
  formatPickupCityState,
  getAndFormatEquipmentType,
  getCommodityFromFreightOrder,
  getWeightFromFreightOrder,
} from 'clerk_common/templates/freight_order/fieldExtractors/fieldExtractors';
import { CompletedFreightOrderTemplate } from 'clerk_common/templates/freight_order/types';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { LuEllipsisVertical } from 'react-icons/lu';
import { PiDotOutlineFill } from 'react-icons/pi';
import { TbArrowNarrowRight } from 'react-icons/tb';
import { ChildSelection } from './JobCard';

interface OrderRowProps {
  order: OrderSummaryFragment;
  asJobChild?: boolean;
  siblingSelectionState?: ChildSelection;
  showReplicatePrompt?: boolean;
}

interface OrderRowDetailsProps {
  data: CompletedFreightOrderTemplate;
}

export const OrderRowDetails = ({ data }: OrderRowDetailsProps) => {
  if (!data._value) return null;

  const pickup = formatPickupCityState(data) ?? UNAVAILABLE;
  const delivery = formatDeliveryCityState(data) ?? UNAVAILABLE;
  const equipmentType = getAndFormatEquipmentType(data);
  const commodity = getCommodityFromFreightOrder(data);
  const weight = getWeightFromFreightOrder(data);

  const getOrderDetails = () => {
    const elements = [equipmentType, commodity, weight].filter(
      (e) => typeof e === 'string' && e.trim()
    );
    const interspersedElements: ReactNode[] = [];
    elements
      .filter((el) => el !== UNAVAILABLE)
      .forEach((element, index) => {
        interspersedElements.push(
          <Text
            key={index}
            type="body-xs"
            className="line-clamp-1 break-words text-gray-500"
          >
            {element}
          </Text>
        );
        if (index < elements.length - 1) {
          interspersedElements.push(getElementSeparator());
        }
      });
    return interspersedElements;
  };

  return (
    <>
      <div className="flex flex-row gap-[2px] text-gray-700">
        <Text type="body-xs" isHeavy className="line-clamp-1 break-words">
          {pickup || 'Unknown'}
        </Text>
        <TbArrowNarrowRight />
        <Text type="body-xs" isHeavy className="line-clamp-1 break-words">
          {delivery || 'Unknown'}
        </Text>
      </div>
      <div className="flex flex-row items-center gap-1">
        {getOrderDetails()}
      </div>
    </>
  );
};

export const OrderRow = ({
  order,
  asJobChild,
  showReplicatePrompt,
}: OrderRowProps) => {
  const { sendToast } = useToast();
  const navigateWithReturn = useNavigateWithReturn();
  const [deleteOrder, { loading: deleteLoading }] = useDeleteOrderMutation();
  const [replicateOrder, { loading: replicateLoading }] =
    useReplicateOrderMutation();
  const data = JSON.parse(order?.extractedData ?? '{}');

  const hoverClass = 'hover:shadow-sm cursor-pointer hover:bg-brand-50';
  const paddingClass = asJobChild ? 'pl-8 py-2' : 'p-2';

  const handleDelete = async () => {
    await deleteOrder({
      variables: { input: { id: order.id } },
      refetchQueries: ['GetJobs'],
    });
  };

  const handleBuildTheRest = async () => {
    await replicateOrder({
      variables: { input: { id: order.id } },
      refetchQueries: ['GetJobs'],
    }).then(() => {
      sendToast('Processing has started!', { variant: 'success' });
    });
  };

  const moreMenuLoading = deleteLoading || replicateLoading;
  const moreMenuOptions = [
    { label: 'Build the rest', onPress: handleBuildTheRest },
    { label: 'Delete', onPress: handleDelete, isDestructive: true },
  ];
  const openOrder = () => navigateWithReturn(`${ORDERS_ROUTE}/${order.id}`);

  return (
    <div
      onClick={openOrder}
      className={clsx(
        'flex w-full cursor-pointer flex-row justify-between gap-2 border-gray-200 bg-white',
        hoverClass,
        paddingClass
      )}
    >
      <div className="text-gray-700">
        {asJobChild ? (
          <PiDotOutlineFill />
        ) : (
          <JobIconWithPadding jobType={JobType.ORDERS} />
        )}
      </div>
      <div className="flex w-full flex-col">
        {!asJobChild && (
          <div className="flex w-full flex-row items-center justify-between">
            <Text type="body-xs" className="line-clamp-1 text-gray-700" isHeavy>
              {order.originator?.name ?? 'Unknown customer'}
            </Text>
            <div className="flex flex-row items-center gap-1">
              <MoreMenu
                moreIcon={<LuEllipsisVertical size={12} />}
                options={moreMenuOptions}
                isLoading={moreMenuLoading}
              />
            </div>
          </div>
        )}
        <div>
          <OrderRowDetails data={data as CompletedFreightOrderTemplate} />
          <div className="flex flex-row flex-wrap justify-between py-1 pr-2">
            <OrderStatusChip orderEvents={order.orderEvents} />
            {showReplicatePrompt && (
              <Button onPress={openOrder} variant="tertiary" size="xs">
                More orders detected
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
